import type { ReactNode } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  /** Что именно произойдёт — словами и по возможности с именем адресата. */
  description: ReactNode;
  confirmLabel: string;
  /** Красная кнопка — для действий, отбирающих доступ. */
  destructive?: boolean;
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * Короткое подтверждение действия администратора платформы (AK-572, ревью
 * head-web).
 *
 * Отдельный компонент, а не пять похожих диалогов: подтверждают здесь вещи
 * одного класса — «человек или целая компания сейчас потеряет доступ». Когда
 * такие действия живут пунктом выпадающего меню или кнопкой в плотной строке
 * списка, промах мышью стоит рабочего дня чужих людей.
 *
 * Ввода названия тут нет намеренно — этим защищена только архивация, самое
 * дорогое по откату. Остальное снимается тем же кликом обратно, и требовать
 * набор текста значило бы приучить набирать не глядя.
 */
export function ConfirmActionDialog({
  open,
  title,
  description,
  confirmLabel,
  destructive = false,
  pending = false,
  error = null,
  onConfirm,
  onOpenChange,
}: ConfirmActionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={pending ? () => {} : onOpenChange}>
      <DialogContent className="max-h-[85dvh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Отмена
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={pending}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmActionDialog;
